import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2,
  Users,
  Mail,
  Phone,
  CreditCard,
  Calendar,
  MapPin,
  Briefcase,
  ChevronRight,
  ChevronLeft,
  Check,
  Clock
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StepIndicator = ({ currentStep, totalSteps, stepTitles }) => (
  <div className="flex items-center justify-center mb-6">
    {stepTitles.map((title, index) => (
      <div key={title} className="flex items-center">
        <div className="flex flex-col items-center">
          <div 
            className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all
              ${index < currentStep 
                ? 'bg-[#467322] text-white' 
                : index === currentStep 
                  ? 'bg-[#467322] text-white ring-4 ring-[#467322]/20' 
                  : 'bg-gray-200 text-gray-500'
              }`}
          >
            {index < currentStep ? <Check size={18} /> : index + 1}
          </div>
          <span className={`text-xs mt-1 font-medium ${index === currentStep ? 'text-[#467322]' : 'text-gray-500'}`}>
            {title}
          </span>
        </div>
        {index < totalSteps - 1 && (
          <div className={`w-12 h-1 mx-2 rounded ${index < currentStep ? 'bg-[#467322]' : 'bg-gray-200'}`} />
        )}
      </div>
    ))}
  </div>
);

const DistributorsPage = () => {
  const { t } = useLanguage();
  const [distributors, setDistributors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLoginHistoryModal, setShowLoginHistoryModal] = useState(false);
  const [loginHistory, setLoginHistory] = useState({ last_login: null, history: [] });
  const [loginHistoryLoading, setLoginHistoryLoading] = useState(false);
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [createStep, setCreateStep] = useState(0);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    cedula: '',
    distributor_type: '',
    address: '',
    password: ''
  });

  const stepTitles = ['Acceso', 'Información'];

  useEffect(() => {
    fetchDistributors();
  }, []);

  const fetchDistributors = async () => {
    try {
      const response = await axios.get(`${API}/users/distributors`);
      setDistributors(response.data);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      toast.error('Error al cargar distribuidores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, {
        ...formData,
        user_type: 'distributor'
      });
      toast.success(t('savedSuccessfully'));
      setShowCreateModal(false);
      setCreateStep(0);
      resetForm();
      fetchDistributors();
    } catch (error) {
      console.error('Error creating distributor:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) delete updateData.password;
      
      await axios.put(`${API}/users/${selectedDistributor.id}`, updateData);
      toast.success(t('savedSuccessfully'));
      setShowProfileModal(false);
      setIsEditing(false);
      fetchDistributors();
    } catch (error) {
      console.error('Error updating distributor:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/users/${selectedDistributor.id}`);
      toast.success(t('deletedSuccessfully'));
      setShowDeleteDialog(false);
      setSelectedDistributor(null);
      fetchDistributors();
    } catch (error) {
      console.error('Error deleting distributor:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const openProfile = (distributor) => {
    setSelectedDistributor(distributor);
    setFormData({
      name: distributor.name,
      email: distributor.email,
      phone: distributor.phone || '',
      cedula: distributor.cedula || '',
      distributor_type: distributor.distributor_type || '',
      address: distributor.address || '',
      password: ''
    });
    setIsEditing(false);
    setShowProfileModal(true);
  };

  const openLoginHistory = async (distributor) => {
    setSelectedDistributor(distributor);
    setLoginHistoryLoading(true);
    setShowLoginHistoryModal(true);
    try {
      const res = await axios.get(`${API}/auth/login-history/${distributor.id}`);
      setLoginHistory(res.data);
    } catch {
      toast.error('Error al cargar historial');
    } finally {
      setLoginHistoryLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      cedula: '',
      distributor_type: '',
      address: '',
      password: '' 
    });
  };

  const openCreateModal = () => {
    resetForm();
    setCreateStep(0);
    setShowCreateModal(true);
  };

  const canProceedStep = () => {
    if (createStep === 0) {
      return formData.email && formData.password;
    }
    return true;
  };

  const nextStep = () => {
    if (createStep < 1) setCreateStep(createStep + 1);
  };

  const prevStep = () => {
    if (createStep > 0) setCreateStep(createStep - 1);
  };

  const filteredDistributors = distributors.filter(d =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (d.cedula && d.cedula.includes(searchTerm))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  const renderCreateStep = () => {
    switch (createStep) {
      case 0:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-[#467322]">Datos de Acceso</h3>
              <p className="text-sm text-gray-500">Información para ingresar a la plataforma</p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-black font-medium">
                Dirección de Correo Electrónico (ID de Acceso) *
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={18} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="correo@ejemplo.com"
                  className="pl-10 border-[#467322]/30 focus:border-[#467322] h-12"
                  data-testid="input-dist-email"
                />
              </div>
              <p className="text-xs text-gray-500">Este será el usuario para iniciar sesión</p>
            </div>

            <div className="space-y-2">
              <Label className="text-black font-medium">Contraseña *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                placeholder="Mínimo 8 caracteres"
                className="border-[#467322]/30 focus:border-[#467322] h-12"
                data-testid="input-dist-password"
              />
            </div>
          </div>
        );
      
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-[#467322]">Información del Distribuidor</h3>
              <p className="text-sm text-gray-500">Datos personales y de contacto</p>
            </div>

            <div className="space-y-2">
              <Label className="text-black font-medium">Nombre y Apellido *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="Nombre completo"
                className="border-[#467322]/30 focus:border-[#467322] h-12"
                data-testid="input-dist-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-black font-medium">Cédula / Pasaporte</Label>
                <Input
                  value={formData.cedula}
                  onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                  placeholder="Número de ID"
                  className="border-[#467322]/30 focus:border-[#467322] h-12"
                  data-testid="input-dist-cedula"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-black font-medium">Celular</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                  className="border-[#467322]/30 focus:border-[#467322] h-12"
                  data-testid="input-dist-phone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-black font-medium">Tipo de Distribuidor</Label>
              <Select 
                value={formData.distributor_type} 
                onValueChange={(value) => setFormData({ ...formData, distributor_type: value })}
              >
                <SelectTrigger className="border-[#467322]/30 focus:border-[#467322] h-12" data-testid="select-dist-type">
                  <SelectValue placeholder="Seleccionar tipo" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#467322]/20">
                  <SelectItem value="Entrenador">Entrenador</SelectItem>
                  <SelectItem value="Vendedor">Vendedor</SelectItem>
                  <SelectItem value="Gimnasio">Gimnasio</SelectItem>
                  <SelectItem value="Medico">Médico</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-black font-medium">Dirección</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Dirección completa"
                className="border-[#467322]/30 focus:border-[#467322] h-12"
                data-testid="input-dist-address"
              />
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="distributors-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('distributors')}
          </h1>
          <p className="text-gray-600 mt-1">{t('distributorsList')}</p>
        </div>
        <Button 
          onClick={openCreateModal}
          className="bg-[#467322] hover:bg-[#3A601C] text-white"
          data-testid="create-distributor-btn"
        >
          <Plus size={20} className="mr-2" />
          {t('newDistributor')}
        </Button>
      </div>

      {/* Search */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder={`${t('search')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-distributors"
            />
          </div>
        </CardContent>
      </Card>

      {/* Distributors Table */}
      <Card className="bg-white border border-[#467322]/20">
        <CardHeader>
          <CardTitle className="text-[#467322] flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            <Users size={24} />
            {t('distributorsList')} ({filteredDistributors.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDistributors.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>{t('noDistributors')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="table-striped">
                <TableHeader>
                  <TableRow className="bg-[#467322]">
                    <TableHead className="text-white font-semibold">{t('name')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('email')}</TableHead>
                    <TableHead className="text-white font-semibold">Cédula/ID</TableHead>
                    <TableHead className="text-white font-semibold">Tipo</TableHead>
                    <TableHead className="text-white font-semibold">{t('phone')}</TableHead>
                    <TableHead className="text-white font-semibold">{t('createdAt')}</TableHead>
                    <TableHead className="text-white font-semibold text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDistributors.map((distributor) => (
                    <TableRow key={distributor.id} className="hover:bg-[#F2F7F0]">
                      <TableCell className="font-medium">{distributor.name}</TableCell>
                      <TableCell>{distributor.email}</TableCell>
                      <TableCell>{distributor.cedula || '-'}</TableCell>
                      <TableCell>{distributor.distributor_type || '-'}</TableCell>
                      <TableCell>{distributor.phone || '-'}</TableCell>
                      <TableCell>
                        {new Date(distributor.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openLoginHistory(distributor)}
                            className="text-blue-500 hover:bg-blue-50"
                            title="Historial de accesos"
                            data-testid={`login-history-${distributor.id}`}
                          >
                            <Clock size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openProfile(distributor)}
                            className="text-[#467322] hover:bg-[#F2F7F0]"
                            data-testid={`view-distributor-${distributor.id}`}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedDistributor(distributor);
                              setShowDeleteDialog(true);
                            }}
                            className="text-red-500 hover:bg-red-50"
                            data-testid={`delete-distributor-${distributor.id}`}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Modal - Wizard Style */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) setCreateStep(0);
      }}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322] text-center" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('newDistributor')}
            </DialogTitle>
          </DialogHeader>
          
          <StepIndicator 
            currentStep={createStep} 
            totalSteps={2} 
            stepTitles={stepTitles}
          />
          
          <form onSubmit={(e) => {
            e.preventDefault();
            if (createStep < 1) {
              nextStep();
            } else {
              handleCreate(e);
            }
          }}>
            {renderCreateStep()}
            
            <div className="flex justify-between mt-8 pt-4 border-t border-[#467322]/10">
              {createStep > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  className="border-[#467322] text-[#467322]"
                  data-testid="btn-dist-prev"
                >
                  <ChevronLeft size={18} className="mr-1" />
                  Anterior
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="border-[#467322] text-[#467322]"
                >
                  Cancelar
                </Button>
              )}
              
              {createStep < 1 ? (
                <Button
                  type="submit"
                  disabled={!canProceedStep()}
                  className="bg-[#467322] hover:bg-[#3A601C] text-white"
                  data-testid="btn-dist-next"
                >
                  Siguiente
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              ) : (
                <Button
                  type="submit"
                  disabled={!formData.name}
                  className="bg-[#467322] hover:bg-[#3A601C] text-white"
                  data-testid="save-distributor-btn"
                >
                  <Check size={18} className="mr-1" />
                  Guardar Distribuidor
                </Button>
              )}
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile/Edit Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {isEditing ? t('edit') : t('distributorProfile')}
            </DialogTitle>
          </DialogHeader>
          
          {isEditing ? (
            <form onSubmit={handleUpdate} className="space-y-4">
              <div className="p-3 bg-[#467322]/10 rounded-lg mb-4">
                <p className="text-sm text-[#467322] font-medium">ID de Acceso (Correo)</p>
                <p className="font-semibold text-black">{formData.email}</p>
              </div>

              <div className="space-y-2">
                <Label className="text-black">Nueva Contraseña (dejar vacío para mantener)</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Nueva contraseña"
                  className="border-[#467322]/30 focus:border-[#467322]"
                />
              </div>

              <div className="border-t border-[#467322]/10 pt-4 mt-4">
                <p className="text-sm font-semibold text-[#467322] mb-3">Información del Distribuidor</p>
                
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-black">Nombre y Apellido *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="border-[#467322]/30 focus:border-[#467322]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-black">Cédula / Pasaporte</Label>
                      <Input
                        value={formData.cedula}
                        onChange={(e) => setFormData({ ...formData, cedula: e.target.value })}
                        className="border-[#467322]/30 focus:border-[#467322]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-black">Celular</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="border-[#467322]/30 focus:border-[#467322]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-black">Tipo de Distribuidor</Label>
                    <Select 
                      value={formData.distributor_type} 
                      onValueChange={(value) => setFormData({ ...formData, distributor_type: value })}
                    >
                      <SelectTrigger className="border-[#467322]/30 focus:border-[#467322]">
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-[#467322]/20">
                        <SelectItem value="Entrenador">Entrenador</SelectItem>
                        <SelectItem value="Vendedor">Vendedor</SelectItem>
                        <SelectItem value="Gimnasio">Gimnasio</SelectItem>
                        <SelectItem value="Medico">Médico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-black">Dirección</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="border-[#467322]/30 focus:border-[#467322]"
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsEditing(false)}
                  className="border-[#467322] text-[#467322]"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  className="bg-[#467322] hover:bg-[#3A601C] text-white"
                >
                  {t('save')}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4">
              {/* Datos de Acceso */}
              <div className="p-4 bg-[#467322] rounded-lg">
                <p className="text-white/80 text-sm">ID de Acceso</p>
                <p className="font-semibold text-white text-lg">{selectedDistributor?.email}</p>
              </div>

              {/* Información del Distribuidor */}
              <div className="border border-[#467322]/20 rounded-lg p-4">
                <p className="text-sm font-semibold text-[#467322] mb-3">Información del Distribuidor</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#467322]" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">Nombre</p>
                      <p className="font-medium text-black">{selectedDistributor?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <CreditCard className="text-[#467322]" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">Cédula/Pasaporte</p>
                      <p className="font-medium text-black">{selectedDistributor?.cedula || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="text-[#467322]" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">Celular</p>
                      <p className="font-medium text-black">{selectedDistributor?.phone || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Briefcase className="text-[#467322]" size={18} />
                    <div>
                      <p className="text-xs text-gray-500">Tipo de Distribuidor</p>
                      <p className="font-medium text-black">{selectedDistributor?.distributor_type || '-'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <MapPin className="text-[#467322]" size={18} />
                  <div>
                    <p className="text-xs text-gray-500">Dirección</p>
                    <p className="font-medium text-black">{selectedDistributor?.address || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Fecha de registro */}
              <div className="flex items-center gap-3 p-3 bg-[#F2F7F0] rounded-lg">
                <Calendar className="text-[#467322]" size={18} />
                <div>
                  <p className="text-xs text-gray-500">Fecha de Registro</p>
                  <p className="font-medium text-black">
                    {selectedDistributor?.created_at && new Date(selectedDistributor.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setIsEditing(true)}
                  className="bg-[#467322] hover:bg-[#3A601C] text-white w-full"
                  data-testid="edit-distributor-btn"
                >
                  <Edit size={16} className="mr-2" />
                  {t('edit')}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Login History Modal */}
      <Dialog open={showLoginHistoryModal} onOpenChange={setShowLoginHistoryModal}>
        <DialogContent className="bg-white border border-blue-200 max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-blue-600 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Clock size={22} />
              Historial de Accesos
            </DialogTitle>
          </DialogHeader>

          <div className="p-3 bg-blue-50 rounded-lg mb-2">
            <p className="text-xs text-blue-500 font-medium">Distribuidor</p>
            <p className="font-bold text-gray-900">{selectedDistributor?.name}</p>
            <p className="text-sm text-gray-500">{selectedDistributor?.email}</p>
          </div>

          {loginHistory.last_login && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-2">
              <p className="text-xs text-green-600 font-bold uppercase tracking-wider">Ultimo acceso</p>
              <p className="font-semibold text-gray-900 mt-0.5">
                {new Date(loginHistory.last_login).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                {' a las '}
                {new Date(loginHistory.last_login).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          )}

          {loginHistoryLoading ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-10 h-10 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
            </div>
          ) : loginHistory.history.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Clock size={36} className="mx-auto mb-3 opacity-40" />
              <p className="font-medium">Sin registros de acceso</p>
              <p className="text-sm">Este distribuidor aun no ha iniciado sesion</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-2">
                Todos los accesos ({loginHistory.history.length})
              </p>
              {loginHistory.history.map((entry, idx) => (
                <div key={entry.timestamp || idx} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors border-b border-gray-50"
                  data-testid={`login-entry-${idx}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${idx === 0 ? 'bg-green-500' : 'bg-gray-300'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      {new Date(entry.logged_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(entry.logged_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                      {entry.ip && entry.ip !== 'unknown' && ` · IP: ${entry.ip}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border border-[#467322]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#467322]">{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#467322] text-[#467322]">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-btn"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DistributorsPage;
